import { ITransactionFees } from './../types/model.types';
import { action, computed, observable, set } from 'mobx';
import { TPlanCode } from '~/components/Membership/types/membership.types';
import {
    IAccountSettings,
    IPricingPlan,
    ISubscription,
    ITrial
} from '~/types/model.types';
import {
    FinanceAccountStatus,
    IAccountResponse,
    TWaitlistableFeature,
    TAutomationsMode
} from '~/types/server.types';
import { TFeaturePlan } from '~/components/FeatureRestriction/types/feature-restriction.types';
import { featureVariationsToFeatureRestrictionMap } from '~/utils/account.utils';
import type { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';
import { AutomationsModeModel } from '~/models/automations-mode.model';

export class Account implements IAccountResponse {
    globalContext: AuthenticatedGlobalContextValue;
    _id: string;
    @observable is_subscribed: boolean;
    @observable account_settings: IAccountSettings;
    @observable pricing_plan: IPricingPlan | null;
    @observable has_multiple_brands: boolean;
    @observable has_subscriptions: boolean;
    @observable predicted_member_segment_measurement: string;
    @observable subscription: ISubscription | null;
    @observable subscription_paused_date: string;
    @observable trial: ITrial;
    @observable selected_next_plan_code?: TPlanCode;
    @observable has_selected_next_plan?: boolean;
    @observable enable_flow_flag?: string;
    @observable is_n8n_enabled?: boolean;
    @observable block_files_flag?: string;
    @observable otam_group?: string;
    @observable card_on_file_enabled: boolean;
    @observable transaction_fees: ITransactionFees;
    @observable featurePlan: TFeaturePlan;
    @observable has_free_txn_fees_30d: boolean;
    finance_account_status: FinanceAccountStatus;
    @observable waitlist_registrations: TWaitlistableFeature[] = [];
    @observable automations_mode: TAutomationsMode;
    @observable automationsMode: AutomationsModeModel;
    @observable publishedLeadFormsCount: number;

    constructor(
        account: IAccountResponse,
        globalContext: AuthenticatedGlobalContextValue
    ) {
        this.globalContext = globalContext;
        set(this, account);
        this.automationsMode = new AutomationsModeModel(this.automations_mode);
    }

    @action
    setPricingPlan(pricingPlan: IPricingPlan) {
        this.pricing_plan = pricingPlan;
        if (this.subscription) {
            this.subscription.pricing_plan = pricingPlan;
        }
    }

    @action
    updateAutomationsMode(mode: TAutomationsMode) {
        this.automations_mode = mode;
        this.automationsMode = new AutomationsModeModel(this.automations_mode);
    }

    @action
    updateTransactionFees(transactionFees: ITransactionFees) {
        this.transaction_fees = transactionFees;
    }

    @action
    updateCardOnFileEnabled(isEnabled: boolean) {
        this.card_on_file_enabled = isEnabled;
    }

    @action
    setFeaturePlan(featurePlan: TFeaturePlan) {
        this.featurePlan = featurePlan;
    }

    @computed
    get featureRestrictions() {
        const {
            companyStore: { currCompany }
        } = this.globalContext;

        return featureVariationsToFeatureRestrictionMap(
            this.featurePlan,
            currCompany,
            this
        );
    }

    @action
    setPublishedLeadFormsCount(count: number) {
        this.publishedLeadFormsCount = count;
    }

    @action
    setAccountSetting<T extends keyof IAccountSettings>(
        field: T,
        value: IAccountSettings[T]
    ) {
        this.account_settings[field] = value;
    }

    @action
    updateWaitlistRegistration(
        featureName: TWaitlistableFeature,
        isInWaitlist: boolean
    ) {
        if (isInWaitlist) {
            this.waitlist_registrations.push(featureName);
        } else {
            this.waitlist_registrations = this.waitlist_registrations.filter(
                feature => feature !== featureName
            );
        }
    }

    isInWaitlist(featureName: TWaitlistableFeature) {
        return this.waitlist_registrations.includes(featureName);
    }

    isPremiumOrEssential() {
        const subscription_plan = this.subscription?.plan_code;
        return (
            this.is_subscribed &&
            !!subscription_plan &&
            /premium|essential/i.test(subscription_plan)
        );
    }
}
